import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { klikBiasa } from '../utils/klikBiasa';
import { auth } from '../firebase';
import { getUserRole, ROLES } from '../utils/roles';
import { toast } from './toastBus';
import {
  CATEGORIES, STATUSES, FULFILLMENTS, FULFILLMENT_LABELS, categoryLabel, formatPrice,
  adminListProducts, adminCreateProduct, adminUpdateProduct,
  adminReplaceImage, uploadEbook, adminDeleteProduct,
  adminListOrders, adminRejectOrder, markOrderDelivered,
  adminListStockItems, adminAddStockItems, adminDeleteStockItem, parseStockBlocks,
} from '../services/marketplaceService';

// ═══════════════════════════════════════════════════════════
//  ADMIN MARKETPLACE — manage products + fulfil orders
// ═══════════════════════════════════════════════════════════

const EMPTY_FORM = {
  title: '', description: '', category: 'ebook', fulfillment: 'auto_download',
  pricingMode: 'paid', priceRM: '', originalPriceRM: '', stock: '', status: 'draft',
  affiliateEnabled: false, affiliatePct: '', order: '', internalNotes: '',
  durationDays: '',
};

function defaultFulfillment(category) {
  return CATEGORIES.find((c) => c.id === category)?.defaultFulfillment || 'manual';
}

function tarikhPendek(ts) {
  const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── PANEL STOK AKAUN ───────────────────────────────────────
// Untuk produk `auto_stock` sahaja. Setiap baris = satu akaun SEBENAR yang
// akan diserahkan kepada SEORANG pembeli. Kiraan stok produk diselaraskan oleh
// service setiap kali senarai ini berubah.
// ⛔ Akaun terjual tidak boleh dipadam — pembeli masih membacanya melalui
//    butang "Lihat Akses".
function StokModal({ product, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teks, setTeks] = useState('');
  const [busy, setBusy] = useState(false);
  const berubahRef = useRef(false);

  const blok = parseStockBlocks(teks);
  const tersedia = items.filter((i) => i.status !== 'sold').length;
  const terjual = items.length - tersedia;

  useEffect(() => {
    muat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  async function muat() {
    setLoading(true);
    try {
      setItems(await adminListStockItems(product.id));
    } catch (e) {
      toast.error('Gagal muat stok: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  async function tambah() {
    if (!blok.length) return;
    setBusy(true);
    try {
      const n = await adminAddStockItems(product.id, blok);
      toast.success(`${n} akaun ditambah ke stok.`);
      setTeks('');
      berubahRef.current = true;
      await muat();
    } catch (e) {
      toast.error('Gagal tambah: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function padam(item) {
    if (!window.confirm('Padam akaun ini daripada stok? Tindakan ini kekal.')) return;
    try {
      await adminDeleteStockItem(product.id, item.id);
      berubahRef.current = true;
      await muat();
    } catch (e) {
      toast.error(e?.message || String(e));
    }
  }

  return (
    <div style={S.modalOverlay} onClick={() => !busy && onClose(berubahRef.current)}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Stok akaun — {product.title || '(tiada tajuk)'}</h2>
          <button style={S.iconBtn} onClick={() => !busy && onClose(berubahRef.current)}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div style={S.formBody}>
          <div style={{ display: 'flex', gap: 6 }}>
            <span style={{ ...S.badge, background: tersedia > 0 ? '#ecfdf5' : '#fee2e2', color: tersedia > 0 ? '#047857' : '#991b1b' }}>
              {tersedia} tersedia
            </span>
            <span style={S.badge}>{terjual} terjual</span>
          </div>

          <div style={{ border: '1px dashed #c7d2fe', borderRadius: 10, padding: 12, background: '#f5f3ff' }}>
            <label style={S.label}>Tampal akaun baharu
              <textarea
                style={{ ...S.input, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5, resize: 'vertical' }}
                rows={7}
                placeholder={'contoh@gmail.com\nkata laluan: abc123\n2FA: 1234 5678\n\ncontoh2@gmail.com\nkata laluan: xyz789'}
                value={teks}
                onChange={(e) => setTeks(e.target.value)}
              />
            </label>
            <div style={{ ...S.cardMeta, marginTop: 6 }}>
              Satu akaun satu blok. Pisahkan setiap akaun dengan <b>baris kosong</b> atau baris <code>---</code>.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <button style={{ ...S.primaryBtn, opacity: busy || !blok.length ? 0.5 : 1 }}
                disabled={busy || !blok.length} onClick={tambah}>
                {busy ? 'Menyimpan…' : `Tambah ${blok.length || ''} akaun`.trim()}
              </button>
              {/* Pratonton kiraan — admin nampak pecahan blok SEBELUM menyimpan. */}
              {blok.length > 0 && (
                <span style={S.cardMeta}>
                  Dikesan: {blok.map((b) => b.split('\n')[0].slice(0, 22)).join(' · ')}
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div style={S.muted}>Memuatkan…</div>
          ) : items.length === 0 ? (
            <div style={S.muted}>Belum ada akaun dalam stok. Tampal di atas untuk mula.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map((i) => {
                const sold = i.status === 'sold';
                return (
                  <div key={i.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px',
                    border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5, color: '#111827',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {i.label || '(kosong)'}
                      </div>
                      {sold && (
                        <div style={S.cardMeta}>
                          {i.buyerEmail || 'pembeli'}
                          {i.accessExpiresAt ? ` · sehingga ${tarikhPendek(i.accessExpiresAt)}` : ' · selamanya'}
                        </div>
                      )}
                    </div>
                    <span style={{ ...S.badge, background: sold ? '#f3f4f6' : '#ecfdf5', color: sold ? '#374151' : '#047857' }}>
                      {sold ? 'Terjual' : 'Tersedia'}
                    </span>
                    {!sold && (
                      <button style={{ ...S.iconBtn, color: '#dc2626' }} title="Padam akaun" onClick={() => padam(i)}>
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={S.modalFooter}>
          <button style={S.ghostBtn} onClick={() => onClose(berubahRef.current)}>Tutup</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminMarketplaceView({ initialTab = 'products' }) {
  const role = getUserRole(auth.currentUser?.email);
  const isAdmin = role === ROLES.ADMIN || role === ROLES.MODERATOR;

  /* Tab datang daripada ALAMAT (`/admin/marketplace/{tab}`) — lihat nota
     penuh dalam AdminView.jsx. Keadaan tempatan hanya cerminan alamat. */
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab); // 'products' | 'orders'
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  /* ⛔ Tukar tab = tukar ALAMAT, bukan setTab terus. */
  const bukaTab = (id) => navigate(`/admin/marketplace/${id}`);

  // ─── Products ───
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const imageRef = useRef(null);
  const ebookRef = useRef(null);
  const [stockFor, setStockFor] = useState(null); // produk yang panel stoknya dibuka

  // ─── Orders ───
  const [orderStatus, setOrderStatus] = useState('requested');
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [deliveryText, setDeliveryText] = useState({}); // orderId -> text
  const [busyOrderId, setBusyOrderId] = useState(null);

  // ═══ LOAD ═══
  async function loadProducts() {
    setLoadingProducts(true);
    try {
      setProducts(await adminListProducts());
    } catch (e) {
      toast.error('Gagal muat produk: ' + (e?.message || e));
    } finally {
      setLoadingProducts(false);
    }
  }

  async function loadOrders(status = orderStatus) {
    setLoadingOrders(true);
    try {
      setOrders(await adminListOrders(status));
    } catch (e) {
      toast.error('Gagal muat order: ' + (e?.message || e));
    } finally {
      setLoadingOrders(false);
    }
  }

  useEffect(() => {
    if (isAdmin) loadProducts();
  }, [isAdmin]);
  useEffect(() => {
    if (isAdmin && tab === 'orders') loadOrders(orderStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, orderStatus, isAdmin]);

  if (!isAdmin) {
    return <div style={{ padding: 40, color: '#ef4444' }}>Akses ditolak — admin sahaja.</div>;
  }

  // ═══ FORM ═══
  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(p) {
    setEditingId(p.id);
    setForm({
      title: p.title || '',
      description: p.description || '',
      category: p.category || 'ebook',
      fulfillment: p.fulfillment || defaultFulfillment(p.category),
      pricingMode: p.pricingMode || 'paid',
      priceRM: p.price ? (p.price / 100).toFixed(2) : '',
      originalPriceRM: p.originalPrice ? (p.originalPrice / 100).toFixed(2) : '',
      stock: p.stock == null ? '' : String(p.stock),
      status: p.status || 'draft',
      affiliateEnabled: !!p.affiliateEnabled,
      affiliatePct: p.affiliateRate == null ? '' : String(Math.round(p.affiliateRate * 100)),
      order: p.order == null ? '' : String(p.order),
      internalNotes: '',
      durationDays: p.durationDays == null ? '' : String(p.durationDays),
    });
    setShowForm(true);
  }

  function setField(k, v) {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === 'category') next.fulfillment = defaultFulfillment(v);
      return next;
    });
  }

  function buildPayload() {
    const isFree = form.pricingMode === 'free';
    const priceSen = isFree ? 0 : Math.round(parseFloat(form.priceRM || '0') * 100);
    const origSen = Math.round(parseFloat(form.originalPriceRM || '0') * 100);
    const pct = form.affiliatePct === '' ? null : parseFloat(form.affiliatePct);
    return {
      title: form.title,
      description: form.description,
      category: form.category,
      fulfillment: form.fulfillment,
      pricingMode: form.pricingMode,
      price: Number.isFinite(priceSen) ? priceSen : 0,
      originalPrice: Number.isFinite(origSen) ? origSen : 0,
      stock: form.stock,
      status: form.status,
      affiliateEnabled: form.affiliateEnabled,
      affiliateRate: pct == null || Number.isNaN(pct) ? null : pct / 100, // store as FRACTION
      order: form.order,
      internalNotes: form.internalNotes,
      durationDays: form.durationDays,
    };
  }

  async function handleSave() {
    if (!form.title.trim()) { toast.error('Sila isi tajuk produk.'); return; }
    if (form.affiliatePct !== '' && (parseFloat(form.affiliatePct) <= 0 || parseFloat(form.affiliatePct) > 100)) {
      toast.error('Kadar komisen mesti antara 1–100%.'); return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      // Stok produk auto-serah dikira daripada senarai akaun, bukan ditaip.
      // Panel Stok satu-satunya penulis nombor itu.
      if (payload.fulfillment === 'auto_stock') {
        if (editingId) delete payload.stock;
        else payload.stock = 0;
      }
      const imageFile = imageRef.current?.files?.[0] || null;
      const ebookFile = ebookRef.current?.files?.[0] || null;
      if (ebookFile && ebookFile.type !== 'application/pdf') {
        toast.error('Fail ebook mesti PDF.'); setSaving(false); return;
      }

      if (editingId) {
        await adminUpdateProduct(editingId, payload);
        if (imageFile) await adminReplaceImage(editingId, imageFile);
        if (ebookFile) await uploadEbook(editingId, ebookFile, payload.internalNotes);
        toast.success('Produk dikemaskini.');
      } else {
        await adminCreateProduct(payload, imageFile, ebookFile);
        toast.success('Produk dicipta.');
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      await loadProducts();
    } catch (e) {
      toast.error('Gagal simpan: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(p) {
    if (!window.confirm(`Padam produk "${p.title}"? Tindakan ini kekal.`)) return;
    try {
      await adminDeleteProduct(p);
      toast.success('Produk dipadam.');
      setProducts((arr) => arr.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error('Gagal padam: ' + (e?.message || e));
    }
  }

  // ═══ ORDERS ═══
  async function handleDeliver(order) {
    const info = (deliveryText[order.id] || '').trim();
    const autoStock = order.fulfillment === 'auto_stock';
    // Produk auto-serah tidak perlu apa-apa daripada admin — akaun datang
    // daripada stok. Kotak teks itu nota dalaman sahaja.
    if (order.category !== 'ebook' && !autoStock && !info) {
      toast.error('Sila isi info penghantaran (kredential/akses) dahulu.');
      return;
    }
    setBusyOrderId(order.id);
    try {
      const res = await markOrderDelivered(order.id, info);
      const asas = res?.autoStock
        ? (res.isRenewal ? 'Akses disambung pada akaun sedia ada pembeli.' : 'Satu akaun diserahkan automatik daripada stok.')
        : 'Order ditandakan dihantar.';
      if (res?.commissionCredited) {
        toast.success(`${asas} Komisen ${formatPrice(res.commissionAmountSen)} dikreditkan.`);
      } else {
        toast.success(asas);
      }
      if (res?.autoStock) loadProducts(); // kiraan stok berubah
      setOrders((arr) => arr.filter((x) => x.id !== order.id));
    } catch (e) {
      toast.error('Gagal hantar: ' + (e?.message || e));
    } finally {
      setBusyOrderId(null);
    }
  }

  async function handleReject(order) {
    const reason = window.prompt('Sebab tolak (opsyenal):', '') ?? '';
    setBusyOrderId(order.id);
    try {
      await adminRejectOrder(order.id, reason);
      toast.success('Order ditolak.');
      setOrders((arr) => arr.filter((x) => x.id !== order.id));
    } catch (e) {
      toast.error('Gagal tolak: ' + (e?.message || e));
    } finally {
      setBusyOrderId(null);
    }
  }

  // ═══ RENDER ═══
  return (
    <div className="sesaat-m-tight" style={S.page}>
      <div style={S.headerRow}>
        <h1 style={S.h1}>Kelola Marketplace</h1>
        {tab === 'products' && (
          <button style={S.primaryBtn} onClick={openCreate}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span> Tambah Produk
          </button>
        )}
      </div>

      <div style={S.tabs}>
        {/* Pautan sebenar — klik kanan / Ctrl+klik boleh buka tab baharu. */}
        <a href="/admin/marketplace/products" style={tab === 'products' ? S.tabActive : S.tab}
           onClick={(e) => { if (klikBiasa(e)) { e.preventDefault(); bukaTab('products'); } }}>Products</a>
        <a href="/admin/marketplace/orders" style={tab === 'orders' ? S.tabActive : S.tab}
           onClick={(e) => { if (klikBiasa(e)) { e.preventDefault(); bukaTab('orders'); } }}>Orders</a>
      </div>

      {/* ─── PRODUCTS ─── */}
      {tab === 'products' && (
        <>
          {loadingProducts ? (
            <div style={S.muted}>Memuatkan…</div>
          ) : products.length === 0 ? (
            <div style={S.muted}>Belum ada produk. Klik “Tambah Produk”.</div>
          ) : (
            <div style={S.grid}>
              {products.map((p) => (
                <div key={p.id} style={S.card}>
                  <div style={{ ...S.thumb, backgroundImage: p.imageUrl ? `url(${p.imageUrl})` : 'none' }}>
                    {!p.imageUrl && <span className="material-symbols-outlined" style={{ color: '#9ca3af' }}>image</span>}
                    <span style={S.statusPill(p.status)}>{p.status}</span>
                  </div>
                  <div style={{ padding: 12 }}>
                    <div style={S.cardTitle}>{p.title || '(tiada tajuk)'}</div>
                    <div style={S.cardMeta}>{categoryLabel(p.category)} · {p.pricingMode === 'free' ? 'Percuma' : formatPrice(p.price)}</div>
                    <div style={S.badgeRow}>
                      <span style={S.badge}>{p.stock == null ? 'Stok ∞' : `Stok ${p.stock}`}</span>
                      {p.fulfillment === 'auto_stock' && (
                        <span style={{ ...S.badge, background: '#ecfdf5', color: '#047857' }}>
                          Auto serah{p.durationDays ? ` · ${p.durationDays} hari` : ''}
                        </span>
                      )}
                      {p.affiliateEnabled && (
                        <span style={{ ...S.badge, background: '#ecfdf5', color: '#047857' }}>
                          Affiliate {p.affiliateRate != null ? `${Math.round(p.affiliateRate * 100)}%` : 'global'}
                        </span>
                      )}
                    </div>
                    {p.fulfillment === 'auto_stock' && (
                      <button style={{ ...S.smallBtn, width: '100%', marginTop: 10 }} onClick={() => setStockFor(p)}>
                        Urus Stok Akaun
                      </button>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button style={S.smallBtn} onClick={() => openEdit(p)}>Edit</button>
                      <button style={{ ...S.smallBtn, color: '#dc2626' }} onClick={() => handleDelete(p)}>Padam</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ─── ORDERS ─── */}
      {tab === 'orders' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {['requested', 'delivered', 'rejected'].map((st) => (
              <button key={st} style={orderStatus === st ? S.chipActive : S.chip} onClick={() => setOrderStatus(st)}>
                {st === 'requested' ? 'Diminta' : st === 'delivered' ? 'Dihantar' : 'Ditolak'}
              </button>
            ))}
          </div>
          {loadingOrders ? (
            <div style={S.muted}>Memuatkan…</div>
          ) : orders.length === 0 ? (
            <div style={S.muted}>Tiada order.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {orders.map((o) => (
                <div key={o.id} style={S.orderCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={S.cardTitle}>{o.productTitle}</div>
                      <div style={S.cardMeta}>
                        {categoryLabel(o.category)} · {formatPrice(o.amount)} · {o.buyerEmail}
                      </div>
                      {o.buyerWhatsapp && <div style={S.cardMeta}>WhatsApp: {o.buyerWhatsapp}</div>}
                      {o.affiliateRef && <div style={S.cardMeta}>Ref: {o.affiliateRef}</div>}
                      {o.proofImageUrl && (
                        <a href={o.proofImageUrl} target="_blank" rel="noreferrer" style={S.link}>Lihat bukti bayar</a>
                      )}
                    </div>
                    <span style={S.statusPill(o.status)}>{o.status}</span>
                  </div>

                  {orderStatus === 'requested' && (
                    <div style={{ marginTop: 10 }}>
                      <textarea
                        style={S.textarea}
                        rows={o.category === 'ebook' || o.fulfillment === 'auto_stock' ? 2 : 4}
                        placeholder={o.category === 'ebook'
                          ? 'Nota (opsyenal) — ebook auto-download'
                          : o.fulfillment === 'auto_stock'
                            ? 'Nota dalaman (opsyenal) — akaun diserahkan automatik daripada stok'
                            : 'Info penghantaran: username / password / akses Gmail…'}
                        value={deliveryText[o.id] || ''}
                        onChange={(e) => setDeliveryText((d) => ({ ...d, [o.id]: e.target.value }))}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button
                          style={{ ...S.primaryBtn, opacity: busyOrderId === o.id ? 0.6 : 1 }}
                          disabled={busyOrderId === o.id}
                          onClick={() => handleDeliver(o)}
                        >
                          {busyOrderId === o.id ? 'Memproses…' : 'Tandakan Dihantar'}
                        </button>
                        <button style={S.ghostBtn} disabled={busyOrderId === o.id} onClick={() => handleReject(o)}>Tolak</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ─── FORM MODAL ─── */}
      {showForm && (
        <div style={S.modalOverlay} onClick={() => !saving && setShowForm(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h2 style={{ margin: 0, fontSize: 18 }}>{editingId ? 'Edit Produk' : 'Tambah Produk'}</h2>
              <button style={S.iconBtn} onClick={() => !saving && setShowForm(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div style={S.formBody}>
              <label style={S.label}>Tajuk
                <input style={S.input} value={form.title} onChange={(e) => setField('title', e.target.value)} />
              </label>
              <label style={S.label}>Penerangan
                <textarea style={S.input} rows={3} value={form.description} onChange={(e) => setField('description', e.target.value)} />
              </label>

              <div style={S.row2}>
                <label style={S.label}>Kategori
                  <select style={S.input} value={form.category} onChange={(e) => setField('category', e.target.value)}>
                    {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </label>
                <label style={S.label}>Cara hantar
                  <select style={S.input} value={form.fulfillment} onChange={(e) => setField('fulfillment', e.target.value)}>
                    {FULFILLMENTS.map((f) => (
                      <option key={f} value={f}>{FULFILLMENT_LABELS[f] || f}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div style={S.row2}>
                <label style={S.label}>Mod harga
                  <select style={S.input} value={form.pricingMode} onChange={(e) => setField('pricingMode', e.target.value)}>
                    <option value="paid">Bayar</option>
                    <option value="free">Percuma</option>
                  </select>
                </label>
                <label style={S.label}>Status
                  <select style={S.input} value={form.status} onChange={(e) => setField('status', e.target.value)}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>

              {form.pricingMode !== 'free' && (
                <div style={S.row2}>
                  <label style={S.label}>Harga (RM)
                    <input style={S.input} type="number" step="0.01" value={form.priceRM} onChange={(e) => setField('priceRM', e.target.value)} />
                  </label>
                  <label style={S.label}>Harga asal / coret (RM)
                    <input style={S.input} type="number" step="0.01" value={form.originalPriceRM} onChange={(e) => setField('originalPriceRM', e.target.value)} />
                  </label>
                </div>
              )}

              <div style={S.row2}>
                {form.fulfillment === 'auto_stock' ? (
                  <label style={S.label}>Stok
                    <input style={{ ...S.input, background: '#f3f4f6', color: '#6b7280' }} readOnly disabled
                      value={editingId ? `${form.stock || 0} akaun` : 'Belum ada akaun'} />
                    <span style={S.cardMeta}>
                      Dikira automatik daripada senarai akaun. Guna butang <b>Urus Stok Akaun</b> pada kad produk.
                    </span>
                  </label>
                ) : (
                  <label style={S.label}>Stok (kosong = tanpa had)
                    <input style={S.input} type="number" value={form.stock} onChange={(e) => setField('stock', e.target.value)} />
                  </label>
                )}
                <label style={S.label}>Susunan (order)
                  <input style={S.input} type="number" value={form.order} onChange={(e) => setField('order', e.target.value)} />
                </label>
              </div>

              {form.fulfillment === 'auto_stock' && (
                <label style={S.label}>Tempoh akses (hari) — kosong = milik selamanya
                  <input style={S.input} type="number" min="1" placeholder="cth 30 untuk sewa sebulan"
                    value={form.durationDays} onChange={(e) => setField('durationDays', e.target.value)} />
                  <span style={S.cardMeta}>
                    Isi <b>30</b> untuk sewa bulanan (Flow Ultra) — pembeli berhenti nampak kredential selepas tamat.
                    Biar <b>kosong</b> untuk jual putus (akaun TikTok).
                  </span>
                </label>
              )}

              {/* Affiliate */}
              <div style={S.affiliateBox}>
                <label style={{ ...S.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={form.affiliateEnabled} onChange={(e) => setField('affiliateEnabled', e.target.checked)} />
                  Benarkan affiliate promosikan produk ini
                </label>
                {form.affiliateEnabled && (
                  <label style={S.label}>Kadar komisen khas (%) — kosong = guna kadar global (25%)
                    <input style={S.input} type="number" step="1" min="1" max="100" placeholder="cth 40"
                      value={form.affiliatePct} onChange={(e) => setField('affiliatePct', e.target.value)} />
                  </label>
                )}
              </div>

              {/* Image */}
              <label style={S.label}>Gambar produk {editingId ? '(biar kosong kalau tak nak tukar)' : ''}
                <input ref={imageRef} type="file" accept="image/*" style={{ marginTop: 4 }} />
              </label>

              {/* Ebook PDF (only for ebook) */}
              {form.category === 'ebook' && (
                <label style={S.label}>Fail ebook (PDF) {editingId ? '(biar kosong kalau tak nak tukar)' : ''}
                  <input ref={ebookRef} type="file" accept="application/pdf" style={{ marginTop: 4 }} />
                </label>
              )}

              <label style={S.label}>Nota dalaman (admin sahaja)
                <textarea style={S.input} rows={2} value={form.internalNotes} onChange={(e) => setField('internalNotes', e.target.value)} />
              </label>
            </div>

            <div style={S.modalFooter}>
              <button style={S.ghostBtn} onClick={() => setShowForm(false)} disabled={saving}>Batal</button>
              <button style={{ ...S.primaryBtn, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan…' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── PANEL STOK AKAUN ─── */}
      {stockFor && (
        <StokModal
          product={stockFor}
          onClose={(berubah) => {
            setStockFor(null);
            if (berubah) loadProducts(); // kiraan stok + status produk mungkin bertukar
          }}
        />
      )}
    </div>
  );
}

// ═══ STYLES ═══
const S = {
  page: { padding: '24px 28px', maxWidth: 1100, margin: '0 auto' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  h1: { fontSize: 22, fontWeight: 800, margin: 0, color: '#111827' },
  tabs: { display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid #e5e7eb' },
  tab: { padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280', fontWeight: 600, fontSize: 14, textDecoration: 'none', display: 'inline-block' },
  tabActive: { padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#4f46e5', fontWeight: 700, fontSize: 14, borderBottom: '2px solid #4f46e5', textDecoration: 'none', display: 'inline-block' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 },
  card: { border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff' },
  thumb: { height: 120, background: '#f3f4f6 center/cover no-repeat', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' },
  cardTitle: { fontWeight: 700, fontSize: 14, color: '#111827', lineHeight: 1.3 },
  cardMeta: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  badgeRow: { display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' },
  badge: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#f3f4f6', color: '#374151' },
  smallBtn: { flex: 1, padding: '6px 0', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' },
  statusPill: (status) => ({
    position: 'absolute', top: 8, right: 8, fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
    background: status === 'published' ? '#dcfce7' : status === 'sold_out' ? '#fee2e2' : status === 'archived' ? '#e5e7eb' : '#fef9c3',
    color: status === 'published' ? '#166534' : status === 'sold_out' ? '#991b1b' : status === 'archived' ? '#374151' : '#854d0e',
  }),
  orderCard: { border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, background: '#fff' },
  textarea: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' },
  link: { fontSize: 12, color: '#4f46e5', textDecoration: 'underline', display: 'inline-block', marginTop: 4 },
  chip: { padding: '6px 14px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#6b7280' },
  chipActive: { padding: '6px 14px', borderRadius: 999, border: '1px solid #4f46e5', background: '#eef2ff', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#4f46e5' },
  primaryBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  ghostBtn: { padding: '9px 16px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  muted: { color: '#9ca3af', fontSize: 14, padding: '24px 0' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 1000, overflowY: 'auto' },
  modal: { background: '#fff', borderRadius: 16, width: 'min(620px, 100%)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' },
  formBody: { padding: 20, display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '70vh', overflowY: 'auto' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid #e5e7eb' },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#374151' },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  affiliateBox: { border: '1px dashed #c7d2fe', borderRadius: 10, padding: 12, background: '#f5f3ff', display: 'flex', flexDirection: 'column', gap: 10 },
  iconBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280', display: 'flex' },
};
